import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  Col,
  Container,
  Row,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { getCustomerList } from "../../api/customer";
import CandidateRow from "../../helpers/Common/CandidateRow";
import NotFound from "../../helpers/Common/NotFound";
import SmallLoader from "../../helpers/Common/SmallLoader";
import { scrollWindow } from "../../helpers/common";
import Sidebar from "../Jobs/JobListing/Sidebar";
import CandidateSearchOptions from "./CandidateSearchOptions";
import Section from "./Section";

const CandidateList = () => {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);

  // 🟢 Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  const bookmarkReducer = useSelector((state) => state.bookmarkCandidates);
  const bookmarkCandidates = bookmarkReducer?.data;
  const bookmarkLoading = bookmarkReducer?.loading;

  const [searchParams] = useSearchParams();

  useEffect(() => {
    let query = searchParams.get("q");
    let designation = searchParams.get("designation");
    let location = searchParams.get("location");

    // 🟢 Read JobTypeCategoryIds or Industry URL parameters
    let industryParam =
      searchParams.get("JobTypeCategoryIds") || searchParams.get("Industry");

    let workExperiences =
      searchParams.get("workExperiences") || searchParams.get("workExperience");
    let salaryRange =
      searchParams.get("SalaryRange") || searchParams.get("salary_range");
    let DateFilter = searchParams.get("DateFilter");
    let gender = searchParams.get("gender");

    let params = [];

    params.push("AdditionalFieldsSearch[is_required_fields_completed]=yes");

    if (query) {
      params.push(`CustomersSearch[q]=${encodeURIComponent(query)}`);
    }
    if (designation) {
      params.push(`designation=${encodeURIComponent(designation)}`);
    }
    if (DateFilter) {
      params.push(`DateFilter=${DateFilter}`);
    }
    if (location) {
      params.push(`locationName=${encodeURIComponent(location)}`);
    }

    // Pass Industry IDs to API if backend supports it
    if (industryParam) {
      params.push(`Industry=${encodeURIComponent(industryParam)}`);
      params.push(`JobTypeCategoryIds=${encodeURIComponent(industryParam)}`);
    }

    if (salaryRange && salaryRange.trim()) {
      const selectedRanges = salaryRange.split(",");
      selectedRanges.forEach((range) => {
        if (range.trim()) {
          params.push(`SalaryRange=${encodeURIComponent(range.trim())}`);
        }
      });
    }

    if (workExperiences && workExperiences.trim()) {
      const selectedExperiences = workExperiences.split(",");
      selectedExperiences.forEach((exp) => {
        if (exp.trim()) {
          params.push(`workExperiences=${encodeURIComponent(exp.trim())}`);
        }
      });
    }

    if (gender) {
      params.push(`gender=${gender}`);
    }

    const newParams = `&${params.join("&")}`;
    let sort = "-id";

    setLoading(true);

    getCustomerList(2, newParams, 1000, 1, sort)
      .then((res) => {
        const apiCandidates = res?.data?.data || [];
        const newCandidates = [];
        const bookmarkedCandidatesMap = new Map();

        const safeBookmarks = bookmarkCandidates?.data?.data || [];

        // 1. Map bookmarked candidates
        safeBookmarks.forEach((bookmarked) => {
          bookmarkedCandidatesMap.set(bookmarked?.candidate?.id, bookmarked);
        });

        // 2. Unshift bookmarked candidates
        apiCandidates.forEach((item) => {
          if (bookmarkedCandidatesMap.has(item?.id)) {
            newCandidates.unshift(item);
          }
        });

        // 3. Push remaining candidates
        apiCandidates.forEach((item) => {
          if (!bookmarkedCandidatesMap.has(item?.id)) {
            newCandidates.push(item);
          }
        });

        setCandidates(newCandidates);
      })
      .catch((err) => {})
      .finally(() => {
        setLoading(false);
      });

    scrollWindow();
  }, [bookmarkCandidates, searchParams]);

  // 🟢 FRONTEND FILTERING LOGIC FOR INDUSTRY TYPE
  const selectedIndustryIds = (
    searchParams.get("JobTypeCategoryIds") ||
    searchParams.get("Industry") ||
    ""
  )
    .split(",")
    .filter(Boolean);

  const filteredCandidates = candidates.filter((item) => {
    if (selectedIndustryIds.length === 0) return true;

    // Extract industry ID from flat or nested structure
    const candidateSource = item?.candidate || item;
    const rawAdditionalFields =
      item?.candidateAdditionalFields ?? item?.additional_fields;
    const additionalFields =
      typeof rawAdditionalFields === "string"
        ? rawAdditionalFields
          ? JSON.parse(rawAdditionalFields)
          : {}
        : rawAdditionalFields || {};

    const candidateIndustryId = String(
      candidateSource?.industry || additionalFields?.industry || "",
    );

    return selectedIndustryIds.includes(candidateIndustryId);
  });

  // 🟢 Reset to page 1 whenever filters or items change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchParams, candidates.length]);

  // 🟢 Calculate Pagination Slicing
  const totalPages = Math.ceil(filteredCandidates.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedCandidates = filteredCandidates.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      scrollWindow();
    }
  };

  // Helper function to build dynamic numbered pagination buttons with windowing
  const renderPaginationItems = () => {
    const pages = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // First page jump if far away
    if (startPage > 1) {
      pages.push(
        <PaginationItem key={1}>
          <PaginationLink onClick={() => handlePageChange(1)}>1</PaginationLink>
        </PaginationItem>,
      );
      if (startPage > 2) {
        pages.push(
          <PaginationItem disabled key="start-ellipsis">
            <PaginationLink>...</PaginationLink>
          </PaginationItem>,
        );
      }
    }

    // Numbered page links
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <PaginationItem active={i === currentPage} key={i}>
          <PaginationLink onClick={() => handlePageChange(i)}>
            {i}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    // Last page jump if far away
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(
          <PaginationItem disabled key="end-ellipsis">
            <PaginationLink>...</PaginationLink>
          </PaginationItem>,
        );
      }
      pages.push(
        <PaginationItem key={totalPages}>
          <PaginationLink onClick={() => handlePageChange(totalPages)}>
            {totalPages}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    return pages;
  };

  const renderListing = () => {
    if (loading) {
      return (
        <div style={{ textAlign: "center", marginTop: 110 }}>
          <SmallLoader />
        </div>
      );
    }

    if (!filteredCandidates || filteredCandidates.length === 0) {
      return (
        <NotFound
          icon="ic:round-people-alt"
          message="Sorry! Candidate list not found."
        />
      );
    }

    return (
      <React.Fragment>
        {/* Render 20 candidate items per page */}
        {paginatedCandidates.map((details, index) => (
          <CandidateRow
            details={details}
            key={(details?.id || index) + "-" + index}
            isBookmarkUpdating={bookmarkLoading}
          />
        ))}

        {/* Modern Bootstrap Numbered Pagination */}
        {totalPages > 1 && (
          <Row className="mt-4 pt-2">
            <Col lg={12}>
              <div className="d-flex flex-column flex-sm-row justify-content-between align-items-center gap-3">
                <span className="text-muted small">
                  Showing <b>{startIndex + 1}</b> to{" "}
                  <b>
                    {Math.min(
                      startIndex + itemsPerPage,
                      filteredCandidates.length,
                    )}
                  </b>{" "}
                  of <b>{filteredCandidates.length}</b> candidates
                </span>

                <Pagination className="pagination-rounded justify-content-center mb-0">
                  <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink first onClick={() => handlePageChange(1)} />
                  </PaginationItem>
                  <PaginationItem disabled={currentPage === 1}>
                    <PaginationLink
                      previous
                      onClick={() => handlePageChange(currentPage - 1)}
                    />
                  </PaginationItem>

                  {renderPaginationItems()}

                  <PaginationItem disabled={currentPage === totalPages}>
                    <PaginationLink
                      next
                      onClick={() => handlePageChange(currentPage + 1)}
                    />
                  </PaginationItem>
                  <PaginationItem disabled={currentPage === totalPages}>
                    <PaginationLink
                      last
                      onClick={() => handlePageChange(totalPages)}
                    />
                  </PaginationItem>
                </Pagination>
              </div>
            </Col>
          </Row>
        )}
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      <Helmet>
        <title>Candidate List | Webvi </title>
      </Helmet>
      <Section />

      <section className="section">
        <Container>
          <Row>
            <Sidebar source="candidates-page" />
            <Col lg={9}>
              <CandidateSearchOptions />
              {renderListing()}
            </Col>
          </Row>
        </Container>
      </section>
    </React.Fragment>
  );
};

export default CandidateList;
